import {
  BoldOutlined,
  ItalicOutlined,
  MessageOutlined,
  SnippetsOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
} from '@ant-design/icons';
import LinkPopover from '@components/Tiptap/MenuBar/LinkPopover';
import { menuBarStateSelector } from '@components/Tiptap/MenuBar/menu-bar-state-selector';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { Button, Tooltip } from 'antd';
import type { FC } from 'react';
import { useMemo } from 'react';

import styles from './MenuBar.module.scss';

interface OwnProps {
  editor: Editor;
}

const MenuBar: FC<OwnProps> = ({ editor }) => {
  const editorState = useEditorState({
    editor,
    selector: menuBarStateSelector,
  });

  const editorActions = useMemo(
    () => ({
      onBold: () => editor.chain().focus().extendMarkRange('bold').toggleBold().run(),
      onItalic: () => editor.chain().focus().extendMarkRange('italic').toggleItalic().run(),
      onUnderline: () => editor.chain().focus().extendMarkRange('underline').toggleUnderline().run(),
      onStrikethrough: () => editor.chain().focus().extendMarkRange('strike').toggleStrike().run(),
      onCode: () => editor.chain().focus().extendMarkRange('code').toggleCode().run(),
      onBlockquote: () => editor.chain().focus().toggleBlockquote().run(),
    }),
    [editor],
  );

  if (!editor) {
    return null;
  }

  return (
    <div className={styles.menuBar}>
      <div className={styles.controlGroup}>
        <Tooltip title="Bold (Ctrl+B)">
          <Button
            icon={<BoldOutlined />}
            disabled={!editorState.canBold}
            type={editorState.isBold ? 'primary' : 'default'}
            onClick={editorActions.onBold}
            aria-label="Bold (Ctrl+B)"
          />
        </Tooltip>
        <Tooltip title="Italic (Ctrl+I)">
          <Button
            icon={<ItalicOutlined />}
            disabled={!editorState.canItalic}
            type={editorState.isItalic ? 'primary' : 'default'}
            onClick={editorActions.onItalic}
            aria-label="Italic (Ctrl+I)"
          />
        </Tooltip>
        <Tooltip title="Underline (Ctrl+U)">
          <Button
            icon={<UnderlineOutlined />}
            disabled={!editorState.canUnderline}
            type={editorState.isUnderline ? 'primary' : 'default'}
            onClick={editorActions.onUnderline}
            aria-label="Underline (Ctrl+U)"
          />
        </Tooltip>
        <Tooltip title="Strikethrough (Ctrl+Shift+S)">
          <Button
            icon={<StrikethroughOutlined />}
            disabled={!editorState.canStrike}
            type={editorState.isStrike ? 'primary' : 'default'}
            onClick={editorActions.onStrikethrough}
            aria-label="Strikethrough (Ctrl+Shift+S)"
          />
        </Tooltip>
      </div>
      <div className={styles.controlGroup}>
        <Tooltip title="Code (Ctrl+E)">
          <Button
            icon={<SnippetsOutlined />}
            disabled={!editorState.canCode}
            type={editorState.isCode ? 'primary' : 'default'}
            onClick={editorActions.onCode}
            aria-label="Code (Ctrl+E)"
          />
        </Tooltip>
        <Tooltip title="Blockquote (Ctrl+Shift+B)">
          <Button
            icon={<MessageOutlined />}
            disabled={!editorState.canBlockquote}
            type={editorState.isBlockquote ? 'primary' : 'default'}
            onClick={editorActions.onBlockquote}
            aria-label="Blockquote (Ctrl+Shift+B)"
          />
        </Tooltip>
        <LinkPopover editor={editor} />
      </div>
    </div>
  );
};

export default MenuBar;
